<template>
  <div class="travel-block">
    <!-- Header -->
    <div class="order-header d-flex justify-space-between align-center mb-4">
      <div class="d-flex align-center">
        <v-icon size="18" color="#F4811F" class="mr-2">mdi-car-side</v-icon>
        <span class="shop-name font-weight-bold">#{{ travel.travelNumber || 'TRIP-' + travel.id.slice(-6).toUpperCase() }}</span>
      </div>
      <div class="order-status" :style="{ color: statusColor }">
        {{ statusLabel }}
      </div>
    </div>

    <!-- Route -->
    <div class="route-wrap mb-4">
      <div class="route-row mb-2">
        <div class="route-dot route-dot--origin mr-2"></div>
        <div class="route-text">
          <div class="caption grey--text">จุดรับ</div>
          <div class="text-body-2">{{ travel.addressSnapshotNow || '—' }}</div>
        </div>
      </div>
      <div class="route-connector ml-1 mb-2"></div>
      <div class="route-row">
        <div class="route-dot route-dot--dest mr-2"></div>
        <div class="route-text">
          <div class="caption grey--text">จุดส่ง</div>
          <div class="text-body-2">{{ travel.addressSnapshotStart || '—' }}</div>
        </div>
      </div>
    </div>

    <!-- Info strip -->
    <div class="info-strip mb-4">
      <div class="info-item">
        <v-icon size="14" color="#9ca3af" class="mr-1">mdi-map-marker-distance</v-icon>
        <span>{{ travel.distance }} กม.</span>
      </div>
      <div class="info-item">
        <v-icon size="14" color="#9ca3af" class="mr-1">mdi-calendar-outline</v-icon>
        <span>{{ formatDate(travel.createdAt) }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="order-footer d-flex align-center justify-space-between">
      <div class="order-summary">
        ค่าบริการ:
        <span class="font-weight-bold" style="color: #F4811F">
          ฿{{ (travel.price || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }) }}
        </span>
      </div>

      <div class="action-btns d-flex" style="gap: 8px">
        <!-- Cancel while seeking -->
        <v-btn
          v-if="travel.status === 'seeking_rider'"
          outlined small color="error"
          class="btn-action rounded-lg text-none"
          height="36"
          :loading="cancelling"
          @click="cancelTravel"
        >
          ยกเลิก
        </v-btn>

        <!-- Review rider -->
        <v-btn
          v-if="canReview"
          small color="#F4811F" dark elevation="0"
          class="btn-action rounded-lg text-none"
          height="36"
          @click="showReviewDialog = true"
        >
          <v-icon left small>mdi-star-outline</v-icon>รีวิว
        </v-btn>
        <v-chip v-else-if="travel.status === 'completed' && travel.riderReview" x-small label color="#f0fdf4" text-color="#16a34a" class="align-self-center">
          <v-icon left size="12">mdi-check-circle-outline</v-icon>รีวิวแล้ว
        </v-chip>

        <!-- View detail -->
        <v-btn
          outlined small color="grey darken-1"
          class="btn-action rounded-lg text-none"
          height="36"
          :to="{ name: 'travel_detail', params: { id: travel.id } }"
        >
          ดูรายละเอียด
        </v-btn>
      </div>
    </div>

    <!-- Review Dialog -->
    <v-dialog v-model="showReviewDialog" max-width="480px" persistent>
      <v-card class="rounded-xl pa-0">
        <div class="d-flex align-center justify-space-between px-6 pt-5 pb-4">
          <div class="d-flex align-center" style="gap:10px">
            <div style="width:40px;height:40px;background:linear-gradient(135deg,#F4811F,#e06000);border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
              <v-icon color="white" size="20">mdi-star-outline</v-icon>
            </div>
            <div>
              <div style="font-size:15px;font-weight:800;color:#0f172a">รีวิวไรเดอร์</div>
              <div style="font-size:11px;color:#94a3b8">{{ riderName }}</div>
            </div>
          </div>
          <v-btn icon small @click="showReviewDialog = false"><v-icon color="#94a3b8">mdi-close</v-icon></v-btn>
        </div>
        <v-divider />
        <div class="px-6 py-5">
          <div class="d-flex align-center mb-4" style="gap:10px">
            <v-avatar size="44" color="#f1f5f9">
              <v-img v-if="travel.rider?.profileImage" :src="travel.rider.profileImage" />
              <v-icon v-else color="#94a3b8" size="24">mdi-account-outline</v-icon>
            </v-avatar>
            <div>
              <div style="font-size:14px;font-weight:700;color:#0f172a">{{ riderName }}</div>
              <div style="font-size:11px;color:#94a3b8">ความเร็ว / ความสุภาพ / ความระมัดระวัง</div>
            </div>
          </div>
          <!-- Stars -->
          <div class="d-flex mb-3" style="gap:4px">
            <v-btn v-for="n in 5" :key="n" icon small
              @mouseenter="hovered = n" @mouseleave="hovered = 0" @click="reviewRating = n">
              <v-icon :color="(hovered || reviewRating) >= n ? '#f59e0b' : '#d1d5db'" size="30">
                {{ (hovered || reviewRating) >= n ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </v-btn>
          </div>
          <v-textarea v-model="reviewComment" placeholder="แสดงความคิดเห็นเพิ่มเติม (ไม่บังคับ)"
            outlined dense auto-grow rows="2" hide-details="auto" class="rounded-lg" background-color="#f8fafc" />
        </div>
        <v-divider />
        <v-card-actions class="pa-4 d-flex justify-end" style="gap:8px">
          <v-btn text @click="showReviewDialog = false" :disabled="reviewSaving">ยกเลิก</v-btn>
          <v-btn color="#F4811F" dark class="rounded-lg font-weight-bold px-6" elevation="0"
            :loading="reviewSaving" :disabled="reviewRating === 0"
            @click="submitReview">
            <v-icon left small>mdi-send-outline</v-icon>ส่งรีวิว
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { cancelTravelApi } from "@/services/api/travel";
import { createRiderReviewApi } from "@/services/api/review";
import swalAlert from "@/services/alert/swalServices";

const STATUS_MAP = {
  pending:        { label: "รอชำระเงิน",    color: "#9e9e9e" },
  seeking_rider:  { label: "กำลังหาไรเดอร์", color: "#F4811F" },
  rider_accepted: { label: "ไรเดอร์รับงาน",  color: "#2563EB" },
  in_progress:    { label: "กำลังเดินทาง",   color: "#4F46E5" },
  completed:      { label: "สำเร็จแล้ว",     color: "#16a34a" },
  cancelled:      { label: "ยกเลิกแล้ว",    color: "#9e9e9e" },
};

export default {
  name: "TravelCard",
  props: {
    travel: { type: Object, required: true },
  },
  data() {
    return {
      cancelling: false,
      showReviewDialog: false,
      reviewRating: 0,
      reviewComment: "",
      reviewSaving: false,
      hovered: 0,
    };
  },
  computed: {
    statusLabel() { return STATUS_MAP[this.travel.status]?.label || this.travel.status; },
    statusColor() { return STATUS_MAP[this.travel.status]?.color || "#9e9e9e"; },
    canReview() {
      return this.travel.status === "completed" && !!this.travel.riderId && !this.travel.riderReview;
    },
    riderName() {
      const r = this.travel.rider;
      if (!r) return "ไรเดอร์";
      return (`${r.natId_firstName || ""} ${r.natId_lastName || ""}`).trim() || r.username || "ไรเดอร์";
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("th-TH", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
    async submitReview() {
      if (this.reviewRating === 0 || this.reviewSaving) return;
      this.reviewSaving = true;
      try {
        await createRiderReviewApi({ travelId: this.travel.id, rating: this.reviewRating, comment: this.reviewComment || undefined });
        await swalAlert.Success("รีวิวสำเร็จ", "ขอบคุณสำหรับความคิดเห็นของคุณ");
        this.showReviewDialog = false;
        this.$emit("refresh");
      } catch (e) {
        swalAlert.Error("ส่งรีวิวไม่สำเร็จ", e?.response?.data?.message || "กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.reviewSaving = false;
      }
    },

    async cancelTravel() {
      const confirm = await this.$swal?.fire({
        title: "ยืนยันยกเลิก?",
        text: this.travel.paymentStatus === "paid" ? "ระบบจะคืนเงินเข้า Wallet ของคุณ" : "รายการจะถูกยกเลิก",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ไม่",
      });
      if (!confirm?.isConfirmed) return;

      this.cancelling = true;
      try {
        await cancelTravelApi(this.travel.id, "ผู้ใช้ยกเลิกจากประวัติ");
        this.$emit("refresh");
        this.$swal?.fire({ icon: "success", title: "ยกเลิกสำเร็จ", timer: 1500, showConfirmButton: false });
      } catch (e) {
        this.$swal?.fire({ icon: "error", title: "ยกเลิกไม่สำเร็จ", text: e?.response?.data?.message || e.message });
      } finally {
        this.cancelling = false;
      }
    },
  },
};
</script>

<style scoped>
.route-wrap { padding: 0 2px; }
.route-row { display: flex; align-items: flex-start; }
.route-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }
.route-dot--origin { background: #16a34a; }
.route-dot--dest { background: #dc2626; }
.route-connector { width: 2px; height: 16px; background: #e5e7eb; margin-left: 4px; }
.route-text { flex: 1; min-width: 0; }

.info-strip { display: flex; gap: 16px; }
.info-item { display: flex; align-items: center; font-size: 13px; color: #6b7280; }

.shop-name { font-size: 15px; color: #0f172a; }
.order-status { font-size: 13px; font-weight: 600; }
.order-summary { font-size: 14px; color: #374151; }

.action-btns { flex-wrap: wrap; }
.btn-action { font-size: 13px !important; }
</style>
